import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  Button,
  CircularProgress,
} from "@mui/material";
import { Shield as ShieldIcon } from "@mui/icons-material";
import { adminColors } from "../theme/adminTheme";

/**
 * Confirmation dialog for deleting a chat (soft-delete). Shared by the
 * sidebar's per-thread delete (ChatSidebarItem) and the "Clear Chat" action
 * on the currently open conversation (ChatHeader) — replaces window.confirm
 * in both places. Deliberately not a plain OK/Cancel: the safety-review
 * sentence gets its own visually distinct block instead of being flattened
 * into one paragraph a student skims past, and the destructive action reads
 * as "Delete" (not "OK"), matching the Cancel (text) / destructive
 * (contained, color="error") pattern already used for confirm dialogs
 * elsewhere in the admin surfaces.
 */
export default function ConfirmDeleteChatDialog({
  open,
  chatTitle,
  onCancel,
  onConfirm,
  loading = false,
}) {
  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onCancel}
      maxWidth="xs"
      fullWidth
      aria-labelledby="confirm-delete-chat-title"
      aria-describedby="confirm-delete-chat-description"
    >
      <DialogTitle id="confirm-delete-chat-title" sx={{ fontWeight: 700 }}>
        {chatTitle ? `Delete "${chatTitle}"?` : "Delete this chat?"}
      </DialogTitle>
      <DialogContent>
        <Typography
          id="confirm-delete-chat-description"
          sx={{ color: adminColors.inkSoft, fontSize: "0.95rem", lineHeight: 1.6, mb: 2 }}
        >
          It will be removed from your history.
        </Typography>
        <Box
          sx={{
            display: "flex",
            gap: 1.25,
            alignItems: "flex-start",
            bgcolor: adminColors.accentLight,
            border: `1px solid ${adminColors.accentBorder}`,
            borderRadius: "8px",
            px: 1.75,
            py: 1.5,
          }}
        >
          <ShieldIcon sx={{ color: adminColors.accentDark, fontSize: 18, mt: "1px", flexShrink: 0 }} />
          <Typography sx={{ color: adminColors.ink, fontSize: "0.85rem", lineHeight: 1.6 }}>
            School staff keep access for safety review.
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onCancel} disabled={loading} sx={{ color: adminColors.inkSoft }}>
          Cancel
        </Button>
        <Button
          onClick={onConfirm}
          disabled={loading}
          variant="contained"
          color="error"
          startIcon={loading ? <CircularProgress size={16} sx={{ color: "white" }} /> : null}
        >
          {loading ? "Deleting…" : "Delete"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
